import React, { useRef, useState } from 'react';
import { PhotoIcon } from '@heroicons/react/16/solid';
import { Button } from '../common/Catalyst/button';
import { AmenityReturn, AttachmentReturn } from '../clients/rust-api-client';
import { amenityService } from '../clients/ApiClients';
import AttachmentThumb from '../violation/comments/AttachmentThumb';
import { helpClass } from '../reservation/formStyles';
import { ErrorAlert } from '../common/Custom/ErrorAlert';
import { reservationErrorMessage } from '../reservation/reservationMeta';

/**
 * The amenity's photos (#642), shown to households on its card. Uploaded
 * through the amenity's own photos endpoint and drawn with the same
 * thumbnail the violation attachments use, which fetches the bytes
 * through the authenticated client (an `<img src>` would 401).
 */
interface Props {
  amenity: AmenityReturn;
  readOnly: boolean;
  onChanged: () => void;
}

export const AmenityPhotos: React.FC<Props> = ({ amenity, readOnly, onChanged }) => {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await amenityService.uploadAmenityPhoto({ amenityId: amenity.id, file });
      }
      onChanged();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't upload the photo."));
      onChanged();
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const remove = async (photo: AttachmentReturn) => {
    if (!window.confirm(`Remove ${photo.file_name}?`)) return;
    setBusy(true);
    setError(null);
    try {
      // `photo.id` is the attachment; `file_id` is what the inline route reads.
      await amenityService.deleteAmenityPhoto({ amenityId: amenity.id, attachmentId: photo.id });
      onChanged();
    } catch (err) {
      setError(reservationErrorMessage(err, "Couldn't remove the photo."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-4">
      <p className={helpClass}>Shown to households when they browse amenities. The first photo leads.</p>
      {!readOnly && (
        <div>
          <input
            ref={input}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            className="sr-only"
            id={`amenity-photo-${amenity.id}`}
            onChange={(e) => upload(e.target.files)}
          />
          <Button outline onClick={() => input.current?.click()} disabled={busy}>
            <PhotoIcon />
            {busy ? 'Uploading…' : 'Add photos'}
          </Button>
        </div>
      )}
      <ErrorAlert>{error}</ErrorAlert>
      {amenity.photos.length === 0 ? (
        <p className="text-[13px] text-fg-muted">No photos yet.</p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {[...amenity.photos]
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((p) => (
              <AttachmentThumb key={p.id} attachment={p} onRemove={readOnly ? undefined : remove} />
            ))}
        </div>
      )}
    </div>
  );
};

export default AmenityPhotos;
